/**
 * sensitiveConsent — единственный источник правды о решении посетителя по
 * блоку «Явное согласие на чувствительные данные» (WEB-2158 + WEB-2162 п.3).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ. Решение читают ДВА места: сбор ответов
 * (`answerCollection.collectScreenAnswers`, а через него и отправка почты
 * лидом — `QuizScreenWizard.submitEmailIfPresent`) и рендер самого блока.
 * Копия правила в каждом из них — ровно тот класс, которым уже ломались
 * фильтры ответов (шрам WEB-953: копия фильтра расходится с оригиналом молча).
 *
 * ТРЕТИЙ ЧИТАТЕЛЬ — СЕРВЕР (WEB-2259, закрыто). Решение по ВТОРОЙ галочке
 * («передавать партнёрам») плеер отправляет отдельной записью ответа с типом
 * `SENSITIVE_CONSENT_SHARE_BLOCK_TYPE` (см. `answerCollection.ts`), сервер
 * сохраняет его у посетителя и запирает им ЧЕТЫРЕ двери наружу: партнёрский
 * постбэк, серверные конверсии рекламных сетей (CAPI), поток во внешнюю
 * аналитику клиента и пиксель рекламной сети на странице «спасибо». Вебхуки
 * владельца и его письма при запрете ИДУТ — владелец воронки третьим лицом не
 * считается (решение на сегодня; переключатель — перечни в
 * `backend/src/analytics/constants/third-party-share.ts`). Совпадение имени
 * записи с серверным и полноту перечня дверей держит сторож
 * `backend/src/shared/guards/third-party-share-ends.web2259.spec.ts`.
 *
 * Ревизия 45 (C12) застала этот гейт незакрытым, и докблок тогда честно
 * говорил «такого читателя в коде НЕТ». Правило то же и теперь: комментарий,
 * разошедшийся с кодом, врёт следующему читателю — поэтому строка про
 * читателя меняется вместе с кодом, а не «потом».
 *
 * ДВЕ НЕЗАВИСИМЫЕ ГАЛОЧКИ, а не одна. Закон штата Вашингтон требует раздельное
 * согласие на СБОР и на ПЕРЕДАЧУ третьим лицам (WEB-2162 п.3): посетитель
 * вправе дать первое и не дать второе. Поэтому вторая галочка живёт под
 * СОБСТВЕННЫМ ключом формы, тем же приёмом, что единица измерения пикеров
 * (`pickerUnitValueKey`) и ответы модалок лоадера: писать оба решения в
 * `values[block.id]` значило бы склеить их в одно значение — а «одну галочку на
 * несколько видов согласия потом не расцепить без миграции данных» (WEB-2160).
 *
 * БЕЗ ГАЛОЧКИ ПОСЕТИТЕЛЯ ПУСКАЮТ ДАЛЬШЕ. Развилка п.5 карточки WEB-2158
 * закрыта макетом, принятым владельцем 18.09, в пользу «пускать»: стена перед
 * прохождением убивает конверсию, а правовой эффект даёт именно несохранение
 * ответа. Поэтому блок НЕ обязателен для гейта экрана (`requiredGate`), но
 * запирает сбор ответов своего экрана.
 */
import type { EditorBlock, EditorScreen } from '@/store/editorSlice';

/** Имя типа блока — одно на кабинет, рантайм и схему генерации. */
export const SENSITIVE_CONSENT_BLOCK_TYPE = 'sensitive_consent';

/**
 * Разделитель служебного ключа второй галочки. Разбор ключа живёт рядом со
 * сборкой намеренно — разъехавшийся разделитель даёт молчаливую утечку
 * служебной записи туда, где ждут ответ посетителя (шрам WEB-1011).
 */
const SHARE_KEY_MARK = '::share';

export function sensitiveShareValueKey(blockId: string): string {
  return `${blockId}${SHARE_KEY_MARK}`;
}

export function isSensitiveShareValueKey(key: string): boolean {
  // endsWith, не includes: блок с id, случайно содержащим '::share' в
  // середине, не должен вырезаться из сводки (ревизия десятки 7).
  return key.endsWith(SHARE_KEY_MARK);
}

/** Переключатель автора «Отдельное согласие на передачу партнёрам». */
export function sensitiveShareEnabled(block: EditorBlock): boolean {
  return (block as { sensitiveShareEnabled?: boolean }).sensitiveShareEnabled !== false;
}

export interface SensitiveConsentState {
  /** На экране есть блок явного согласия. */
  present: boolean;
  /** Обработку ответов разрешили (или блока нет — запрещать нечего). */
  collectGranted: boolean;
  /** Передачу третьим лицам разрешили явной второй галочкой. */
  shareGranted: boolean;
  /** Блоки согласия экрана — чтобы их собственное значение не потерялось. */
  consentBlocks: EditorBlock[];
}

function isGranted(value: string | undefined): boolean {
  // Строго 'true': снятая галочка пишет 'false', и эта строка НЕ должна
  // проходить как «непустая» (шрам WEB-895, возврат QA).
  return value === 'true';
}

/**
 * Прочитать решение посетителя по блокам одного экрана.
 *
 * Блока на экране нет → решать нечего: сбор идёт как раньше. Это важно для
 * обратной совместимости — иначе все уже опубликованные воронки перестали бы
 * собирать ответы в день выката.
 */
export function readSensitiveConsent(
  blocks: readonly EditorBlock[],
  values: Record<string, string>,
  hiddenBlockIds: ReadonlySet<string> = new Set(),
): SensitiveConsentState {
  // Ревизия 45 (C11): СКРЫТЫЙ правилом показа блок = отсутствующий блок. Тот
  // же контракт A4, по которому скрытые блоки не идут в аналитику ответов.
  // Иначе блок, спрятанный собственным правилом автора, запирал бы экран
  // навсегда: галочки посетитель не видел и поставить её не мог.
  const consentBlocks = blocks.filter(
    (b) => b.type === SENSITIVE_CONSENT_BLOCK_TYPE && !hiddenBlockIds.has(b.id),
  );
  if (consentBlocks.length === 0) {
    return {
      present: false,
      collectGranted: true,
      shareGranted: true,
      consentBlocks: [],
    };
  }
  // Несколько блоков на экране — согласие считается данным, только когда
  // отмечены ВСЕ: каждый блок описывает своё основание, и «частичное да»
  // согласием не является.
  const collectGranted = consentBlocks.every((b) => isGranted(values[b.id]));
  // Ревизия 45 (C13): у блока с ВЫКЛЮЧЕННЫМ переключателем второй галочки на
  // экране нет — автор про передачу отдельно не спрашивал. Раньше такой блок
  // давал `shareGranted: false` навсегда, то есть выключенный переключатель
  // работал тихим запретом на всю передачу. Не спросили отдельно — действует
  // решение по первой галочке.
  const shareGranted =
    collectGranted &&
    consentBlocks.every(
      (b) =>
        !sensitiveShareEnabled(b) ||
        isGranted(values[sensitiveShareValueKey(b.id)]),
    );
  return { present: true, collectGranted, shareGranted, consentBlocks };
}

/**
 * Ревизия 45 (C6) — РЕШЕНИЕ ДЕЙСТВУЕТ НА ВСЁ ПРОХОЖДЕНИЕ, а не на свой экран.
 *
 * Замок `readSensitiveConsent` судит по блокам ОДНОГО экрана, а
 * автоподстановка ставит ОДИН блок согласия на воронку — перед первым
 * чувствительным вопросом. Отсюда дыра: рост, вес, сон и настроение со
 * СЛЕДУЮЩИХ экранов собирались так, будто согласия никто не спрашивал.
 *
 * Как решаем:
 *  · свой блок на текущем экране главнее — автор вправе спросить отдельное
 *    согласие под конкретный вопрос;
 *  · иначе судит ПЕРВЫЙ блок согласия среди ПРОЙДЕННЫХ экранов (по порядку
 *    экранов). Значение берётся из тех же `values`: форма живёт всё
 *    прохождение, поэтому галочка, поставленная на втором экране, читается и
 *    на пятом;
 *  · блок, скрытый правилом показа, не судит ничего (см. C11 выше).
 *
 * ПРОХОД ПО ШВАМ ПАЧКИ 45, С1 — ТОЛЬКО ПРОЙДЕННЫЕ ЭКРАНЫ. Обход искал блок по
 * ВСЕЙ воронке, включая экраны, до которых посетитель ещё не дошёл.
 * Автоподстановка ставит блок перед ПЕРВЫМ чувствительным вопросом — обычно
 * это экран 4-5 (сначала «какая у вас цель», потом вес и рост). Отсюда тихая
 * потеря: ответы экранов 1-4 и почта с них не сохранялись и не уходили
 * событием, потому что галочку человек ещё даже не видел и поставить её не
 * мог. Блок, до которого не дошли, ничего не запрещает: запрещать нечего —
 * посетителю ещё не задали вопрос.
 *
 * ГРАНИЦА, названная честно: множество пройденных экранов приносит ВЫЗЫВАЮЩИЙ
 * (у плеера это история переходов, а не порядок `screens`, — переходы
 * правилами экраны пропускают, и порядок соврал бы). После «начать заново»
 * история пуста, а `values` живы: ранее данное решение снова начинает
 * действовать только с того экрана, где блок согласия встретится второй раз.
 * Это та же сторона «до блока не запираем», а не отдельное послабление.
 *
 * @param hiddenBlockIds скрытые правилом блоки ВСЕХ пройденных экранов, а не
 *   только текущего: иначе починка C11 работала бы ровно один экран (С2).
 * @param passedScreenIds идентификаторы экранов, которые посетитель уже прошёл
 *   (текущий входит). Параметр обязателен намеренно — умолчания «все экраны»
 *   у него нет, чтобы дыра С1 не вернулась забытым аргументом.
 */
export function resolveFunnelSensitiveConsent(
  screens: readonly EditorScreen[],
  currentBlocks: readonly EditorBlock[],
  values: Record<string, string>,
  hiddenBlockIds: ReadonlySet<string>,
  passedScreenIds: ReadonlySet<string>,
): SensitiveConsentState {
  const own = readSensitiveConsent(currentBlocks, values, hiddenBlockIds);
  if (own.present) return own;

  for (const screen of screens) {
    if (!screen || !passedScreenIds.has(screen.id)) continue;
    const blocks = Array.isArray(screen.blocks) ? screen.blocks : [];
    const state = readSensitiveConsent(blocks, values, hiddenBlockIds);
    if (state.present) return state;
  }
  return own;
}
